import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { storage } from '../lib/firebase';
import { translations } from '../i18n/translations';

export interface VideoUploadResult {
  url: string;
  filename: string;
  size: number;
}

export class VideoUploadService {
  private static readonly MAX_FILE_SIZE = 50 * 1024 * 1024; // 50MB
  private static readonly ALLOWED_TYPES = [
    'video/mp4',
    'video/quicktime',
    'video/x-msvideo',
    'video/webm',
    'video/x-matroska'
  ];

  static validateFile(file: File, language: 'en' | 'fr' = 'en'): string | null {
    const t = translations[language];
    
    // Check file size
    if (file.size > this.MAX_FILE_SIZE) {
      return t.notifications.fileSizeTooLarge.replace('{size}', String(this.MAX_FILE_SIZE / (1024 * 1024)));
    }

    // Check file type
    if (!this.ALLOWED_TYPES.includes(file.type)) {
      return t.notifications.unsupportedFileType;
    }

    return null;
  }

  static validateVideoUrl(url: string, language: 'en' | 'fr' = 'en'): string | null {
    const t = translations[language];
    
    try {
      const urlObj = new URL(url);
      
      // Check if it's a direct video file URL
      const pathname = urlObj.pathname.toLowerCase();
      const hasVideoExtension = /\.(mp4|mov|avi|webm|mkv)$/i.test(pathname);
      
      if (!hasVideoExtension) {
        return t.notifications.invalidVideoUrl;
      }

      // Block YouTube, Vimeo, and other platforms
      const hostname = urlObj.hostname.toLowerCase();
      const blockedDomains = [
        'youtube.com',
        'youtu.be',
        'vimeo.com',
        'twitch.tv',
        'tiktok.com',
        'instagram.com',
        'facebook.com',
        'twitter.com'
      ];

      if (blockedDomains.some(domain => hostname.includes(domain))) {
        return t.notifications.platformUrlNotSupported;
      }

      return null;
    } catch (error) {
      return 'Invalid URL format';
    }
  }

  static async uploadVideo(file: File, gameId: string, userId: string): Promise<VideoUploadResult> {
    // Validate file
    const validationError = this.validateFile(file);
    if (validationError) {
      throw new Error(validationError);
    }

    try {
      // Create unique filename
      const timestamp = Date.now();
      const extension = file.name.split('.').pop() || 'mp4';
      const filename = `${gameId}_${userId}_${timestamp}.${extension}`;
      
      // Create storage reference
      const storageRef = ref(storage, `videos/${gameId}/${filename}`);
      
      // Upload file
      const snapshot = await uploadBytes(storageRef, file);
      
      // Get download URL
      const downloadURL = await getDownloadURL(snapshot.ref);
      
      return {
        url: downloadURL,
        filename: filename,
        size: file.size
      };
    } catch (error) {
      console.error('Video upload error:', error);
      throw new Error(`Failed to upload video: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  }

  static async testVideoUrl(url: string): Promise<boolean> {
    try {
      const response = await fetch(url, { method: 'HEAD' });
      const contentType = response.headers.get('content-type') || '';
      return response.ok && contentType.startsWith('video/');
    } catch (error) {
      return false;
    }
  }
}

export const videoUploadService = new VideoUploadService();